import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Container, 
  Avatar, 
  Chip, 
  Menu, 
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { 
  AccountCircle, 
  Logout, 
  Settings, 
  Language,
  Help,
  Info,
  MenuBook,
  Dashboard,
  People,
  Assessment,
  AccountTree,
  AdminPanelSettings,
  Assignment,
  Engineering,
  Security,
  Topic,
  Source,
  Description,
  Storage,
  Flag,
  MonetizationOn
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import SearchBar from '../Search/SearchBar';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { themePreferences } = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dataManagementAnchorEl, setDataManagementAnchorEl] = useState<null | HTMLElement>(null);
  const [dhsStudyAnchorEl, setDhsStudyAnchorEl] = useState<null | HTMLElement>(null);
  
  // Debug logging
  React.useEffect(() => {
    console.log('👤 Navbar user object:', user);
    console.log('👤 User role:', user?.role);
  }, [user]);
  
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    setAnchorEl(null);
    navigate('/');
  };
  
  const handleProfileClick = () => {
    setAnchorEl(null);
    navigate('/profile');
  };
  
  const handleSettingsClick = () => {
    setAnchorEl(null);
    navigate('/settings');
  };

  const handleDataManagementMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDataManagementAnchorEl(event.currentTarget);
  };

  const handleDataManagementMenuClose = () => {
    setDataManagementAnchorEl(null);
  };

  const handleDataManagementNavigation = (path: string) => {
    setDataManagementAnchorEl(null);
    navigate(path);
  };

  const handleDhsStudyMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDhsStudyAnchorEl(event.currentTarget);
  };

  const handleDhsStudyMenuClose = () => {
    setDhsStudyAnchorEl(null);
  };

  const handleDhsStudyNavigation = (path: string) => {
    setDhsStudyAnchorEl(null);
    navigate(path);
  };
  
  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      {/* USA Banner - matching USCIS style */}
      {themePreferences.showGovernmentBanner && (
        <Box
          sx={{
            backgroundColor: '#f0f0f0',
            borderBottom: '1px solid #ddd',
            py: 0.5,
            px: 2,
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.875rem',
            position: 'fixed',
            top: 0,
            zIndex: (theme) => theme.zIndex.drawer + 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src="/images/USFlag-Icon-2x.png" alt="U.S. flag" style={{ height: 16, width: 'auto' }} />
            <Typography variant="body2" sx={{ color: '#333' }}>
              An official website of the United States government
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              size="small"
              sx={{ 
                textTransform: 'none', 
                color: '#003366',
                fontSize: '0.875rem',
                fontWeight: 400,
                p: 0
              }}
              startIcon={<Language fontSize="small" />}
            >
              English
            </Button>
          </Box>
        </Box>
      )}
      
      {/* Main AppBar - USCIS OPQ style */}
      <AppBar
        position="fixed"
        sx={{
          top: themePreferences.showGovernmentBanner ? 28 : 0, // Account for USA banner if shown
          zIndex: (theme) => theme.zIndex.drawer + 1,
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          backgroundColor: '#fff',
          borderBottom: '2px solid #003366', // USCIS blue border
          width: '100%',
          left: 0,
          right: 0,
          height: 90 // Set a fixed height
        }}
      >
        <Container maxWidth={false} sx={{ pl: 0, ml: 0, px: 2, width: '100%' }}>
          <Toolbar sx={{ px: { xs: 0 }, py: 1, minHeight: '90px', justifyContent: 'flex-start' }}>
            {/* Logo */}
            <Box
              component={Link}
              to="/dashboard"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textDecoration: 'none',
                zIndex: 2
              }}
            >
              <img 
                src="/images/eunify-m.png" 
                alt="E-Unify - Your Pathway to Benefit" 
                style={{ height: 60, width: 'auto' }}
              />
            </Box>
            
            {/* Search bar - USCIS style */}
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', maxWidth: 500, mx: 'auto' }}>
              <SearchBar placeholder="Search our site" />
            </Box>
            
            {/* Utility Navigation - Section 508 compliant */}
            <Box 
              sx={{ 
                display: 'flex', 
                gap: 1, 
                alignItems: 'center', 
                mr: 2, 
                borderRight: '1px solid #e0e0e0',
                pr: 2 
              }}
              aria-label="Utility navigation"
            >
              <Tooltip title="Data Strategy Support Services">
                <Button
                  component={Link}
                  to="/data-strategy-support"
                  size="small"
                  startIcon={<MenuBook />}
                  sx={{
                    color: '#003366',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': { backgroundColor: 'rgba(0, 51, 102, 0.04)' },
                    '&:focus': { outline: '2px solid #003366', outlineOffset: '2px' }
                  }}
                  aria-label="Data Strategy Support Services"
                >
                  Data Strategy
                </Button>
              </Tooltip>
              <Tooltip title="Data Management">
                <Button
                  size="small"
                  startIcon={<Dashboard />}
                  onClick={handleDataManagementMenuOpen}
                  sx={{
                    color: '#003366',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': { backgroundColor: 'rgba(0, 51, 102, 0.04)' },
                    '&:focus': { outline: '2px solid #003366', outlineOffset: '2px' }
                  }}
                  aria-label="Data Management"
                >
                  Data Management
                </Button>
              </Tooltip>
              <Menu
                anchorEl={dataManagementAnchorEl}
                open={Boolean(dataManagementAnchorEl)}
                onClose={handleDataManagementMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem onClick={() => handleDataManagementNavigation('/data-quality')}>
                  <ListItemIcon>
                    <Assessment fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Data Quality</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleDataManagementNavigation('/data-lineage')}>
                  <ListItemIcon>
                    <AccountTree fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Data Lineage</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleDataManagementNavigation('/tech-stack')}>
                  <ListItemIcon>
                    <Engineering fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Tech Stack</ListItemText>
                </MenuItem>
                {user && (user.role === 'admin' || user.role === 'data-steward') && (
                  <MenuItem onClick={() => handleDataManagementNavigation('/data-steward')}>
                    <ListItemIcon>
                      <AdminPanelSettings fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Data Steward</ListItemText>
                  </MenuItem>
                )}
              </Menu>
              <Tooltip title="DHS Study Center">
                <Button
                  size="small"
                  startIcon={<Security />}
                  onClick={handleDhsStudyMenuOpen}
                  sx={{
                    color: '#003366',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': { backgroundColor: 'rgba(0, 51, 102, 0.04)' },
                    '&:focus': { outline: '2px solid #003366', outlineOffset: '2px' }
                  }}
                  aria-label="DHS Study Center"
                >
                  DHS Study
                </Button>
              </Tooltip>
              <Menu
                anchorEl={dhsStudyAnchorEl}
                open={Boolean(dhsStudyAnchorEl)}
                onClose={handleDhsStudyMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem onClick={() => handleDhsStudyNavigation('/dhs')}>
                  <ListItemIcon>
                    <Dashboard fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>DHS Study Home</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => handleDhsStudyNavigation('/dhs/components')}>
                  <ListItemIcon>
                    <People fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>DHS Components</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleDhsStudyNavigation('/dhs/data-topics')}>
                  <ListItemIcon>
                    <Topic fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Data Analysis Topics</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleDhsStudyNavigation('/dhs/data-sources')}>
                  <ListItemIcon>
                    <Source fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Authoritative Data Sources</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleDhsStudyNavigation('/dhs/file-definitions')}>
                  <ListItemIcon>
                    <Description fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>File Definitions Template</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleDhsStudyNavigation('/dhs/systems')}>
                  <ListItemIcon>
                    <Storage fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Systems Inventory</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleDhsStudyNavigation('/dhs/value')}>
                  <ListItemIcon>
                    <Flag fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Mission Value</ListItemText>
                </MenuItem>
              </Menu>
              {user && (user.role === 'admin' || user.role === 'odos-admin' || user.role === 'odos-steward' || user.role === 'odos-tester' || user.role === 'odos-user') && (
                <Tooltip title="ODOS-IV Module Management">
                  <Button
                    component={Link}
                    to="/odos"
                    size="small"
                    startIcon={<Engineering />}
                    sx={{
                      color: '#003366',
                      textTransform: 'none',
                      fontWeight: 500,
                      '&:hover': { backgroundColor: 'rgba(0, 51, 102, 0.04)' },
                      '&:focus': { outline: '2px solid #003366', outlineOffset: '2px' }
                    }}
                    aria-label="ODOS-IV Operations and Support"
                  >
                    ODOS-IV
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="Pricing">
                <Button
                  component={Link}
                  to="/pricing"
                  size="small"
                  startIcon={<MonetizationOn />}
                  sx={{
                    color: '#003366',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': { backgroundColor: 'rgba(0, 51, 102, 0.04)' },
                    '&:focus': { outline: '2px solid #003366', outlineOffset: '2px' }
                  }}
                  aria-label="E-Unify Pricing"
                >
                  Pricing
                </Button>
              </Tooltip>
              <Tooltip title="About E-Unify">
                <Button
                  component={Link}
                  to="/about"
                  size="small"
                  startIcon={<Info />}
                  sx={{
                    color: '#003366',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': { backgroundColor: 'rgba(0, 51, 102, 0.04)' },
                    '&:focus': { outline: '2px solid #003366', outlineOffset: '2px' }
                  }}
                  aria-label="About E-Unify"
                >
                  About
                </Button>
              </Tooltip>
            </Box>
            
            {/* Right side: Sign In/Admin User */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', zIndex: 3 }}>
            {user ? (
              <>
                <Chip
                  avatar={<Avatar alt={user.name} src="/images/user-avatar.png" />}
                  label={user.name}
                  onClick={handleMenuOpen}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                    fontWeight: 600,
                    fontSize: '1rem',
                    py: 0.5,
                    backgroundColor: '#f0f5fb',
                    borderColor: '#003366',
                    color: '#003366'
                  }}
                  aria-label="User profile and settings"
                  role="button"
                  color="primary"
                  variant="outlined"
                />
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <MenuItem onClick={handleProfileClick}>
                    <ListItemIcon>
                      <AccountCircle fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Profile</ListItemText>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => { handleMenuClose(); navigate('/tasks'); }}>
                    <ListItemIcon>
                      <Assignment fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Open Tasks</ListItemText>
                  </MenuItem>
                  {user && (user.role === 'admin' || user.role === 'odos-admin' || user.role === 'data-steward') && (
                    <MenuItem onClick={() => { handleMenuClose(); navigate('/access/user-management'); }}>
                      <ListItemIcon>
                        <People fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Users</ListItemText>
                    </MenuItem>
                  )}
                  {user && (user.role === 'admin' || user.role === 'odos-admin') && (
                    <>
                      <MenuItem onClick={() => { handleMenuClose(); navigate('/admin/menu-management'); }}>
                        <ListItemIcon>
                          <Settings fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Menu</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => { handleMenuClose(); navigate('/admin/team-roster-picklists'); }}>
                        <ListItemIcon>
                          <People fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Team Roster Picklists</ListItemText>
                      </MenuItem>
                    </>
                  )}
                  <Divider />
                  <MenuItem onClick={handleSettingsClick}>
                    <ListItemIcon>
                      <Settings fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>App Settings</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => { handleMenuClose(); navigate('/help'); }}>
                    <ListItemIcon>
                      <Help fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Help Center</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => { handleMenuClose(); navigate('/federal-data-strategy'); }}>
                    <ListItemIcon>
                      <MenuBook fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Federal Data Strategy</ListItemText>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <Logout fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Logout</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                component={Link}
                to="/login"
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Login
              </Button>
            )}

          </Box>
        </Toolbar>
      </Container>
    </AppBar>
    </Box>
  );
};

export default Navbar;
